/**
 * Accent (primary) colour switcher.
 *
 * Only the `--color-primary` token varies between accents — every
 * component paints from it — so switching is just an attribute on
 * <html data-accent="…">, with the actual colours living in CSS
 * (`src/assets/main.css`, kept in sync with `ACCENTS` below).
 *
 * Persisted to localStorage and mirrored by the splash bootstrap so the
 * first paint already carries the chosen accent (no green flash before
 * purple). `green` is the default and needs no attribute.
 */
import { ref, watch } from 'vue'

export type Accent = 'green' | 'purple' | 'indigo'

interface AccentMeta {
  /** Human label for the picker. */
  label: string
  /** Swatch colour (matches `--color-primary` for this accent). */
  swatch: string
}

/** Keep the keys in sync with the `[data-accent='…']` rules in main.css. */
export const ACCENTS: Record<Accent, AccentMeta> = {
  green: { label: 'Green', swatch: 'hsl(142 71% 45%)' },
  purple: { label: 'Purple', swatch: 'hsl(271 81% 56%)' },
  indigo: { label: 'Indigo', swatch: 'hsl(242 56% 58%)' },
}

export const STORAGE_KEY = 'ezqr:accent'

const accent = ref<Accent>('green')

function isAccent(v: unknown): v is Accent {
  return v === 'green' || v === 'purple' || v === 'indigo'
}

function apply(value: Accent) {
  if (typeof document === 'undefined') return
  // Default accent matches @theme, so drop the attribute entirely.
  if (value === 'green') document.documentElement.removeAttribute('data-accent')
  else document.documentElement.setAttribute('data-accent', value)
}

/**
 * Singleton init guard — mirrors `useTheme`. The stored-value read and
 * the persist/apply watcher only need to run once for the whole app
 * (the `accent` ref is module-scope, so all callers share it). Without
 * this, every `useAccent()` consumer (App.vue AND UserMenu) registered
 * its own watcher, so one accent change wrote localStorage and toggled
 * the attribute twice.
 */
let booted = false

function bootstrapOnce(): void {
  if (booted) return
  booted = true

  let stored: string | null = null
  try {
    stored = localStorage.getItem(STORAGE_KEY)
  } catch {
    /* private-mode Safari etc. — fall back to default */
  }
  accent.value = isAccent(stored) ? stored : 'green'
  apply(accent.value)

  watch(accent, (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value)
    } catch {
      /* ignore */
    }
    apply(value)
  })
}

export function useAccent() {
  bootstrapOnce()

  function set(value: Accent) {
    accent.value = value
  }

  return { accent, set, accents: ACCENTS }
}
